import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiPhone, FiMail, FiMapPin, FiSend, FiCheck, FiClock } from 'react-icons/fi';
import emailjs from 'emailjs-com';

/* ── Étoiles locales (dark mode) ── */
const StarsWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
  display: block;
`;

const StarDot = styled.div`
  position: absolute;
  border-radius: 50%;
  background: white;
`;

function SectionStars({ count = 120 }) {
  const stars = useMemo(() => {
    const arr = [];
    for (let i = 0; i < count; i++) {
      const size = Math.random() * 2 + 0.5;
      arr.push({
        id: i,
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        width: size,
        height: size,
        opacity: Math.random() * 0.45 + 0.08,
        animDelay: Math.random() * 6,
        animDuration: Math.random() * 4 + 3,
      });
    }
    return arr;
  }, [count]);

  return (
    <StarsWrapper>
      {stars.map(s => (
        <StarDot
          key={s.id}
          style={{
            left: s.left,
            top: s.top,
            width: s.width,
            height: s.height,
            opacity: s.opacity,
            animation: `twinkle ${s.animDuration}s ease-in-out ${s.animDelay}s infinite alternate`,
          }}
        />
      ))}
    </StarsWrapper>
  );
}

const ContactContainer = styled.section`
  padding: 6rem 0;
  background: linear-gradient(140deg, #1b4570 0%, #205d8a 50%, #2980b9 100%);
  position: relative;
  overflow: hidden;
  scroll-margin-top: 70px;
  
  @media (max-width: 768px) {
    padding: 4rem 0;
    scroll-margin-top: 60px;
  }
  
  body.dark-mode & {
    background: #0e1420;
  }
`;

const ContactWrapper = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 2rem;
  
  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 0 1rem;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  
  @media (max-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.8rem;
  font-weight: 800;
  color: var(--white);
  margin-bottom: 1rem;
  
  span {
    color: var(--secondary);
  }
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

const SectionSubtitle = styled(motion.p)`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.7);
  max-width: 500px;
  margin: 0 auto;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 3rem;
  align-items: start;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ContactInfoCard = styled(motion.div)`
  background: white;
  border-radius: 24px;
  padding: 2.5rem;
  color: var(--dark);
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  
  body.dark-mode & {
    background: #0e1420;
    color: white;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem;
    border-radius: 16px;
  }
`;

const InfoTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  position: relative;
  color: var(--dark);
  
  body.dark-mode & {
    color: white;
  }
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
  }
`;

const InfoSubtitle = styled.p`
  font-size: 0.95rem;
  color: var(--gray-600);
  margin-bottom: 2rem;
  line-height: 1.6;
  
  body.dark-mode & {
    color: var(--gray-400);
  }
`;

const ContactInfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const ContactInfoItem = styled.a`
  display: flex;
  align-items: center;
  gap: 1rem;
  text-decoration: none;
  color: var(--dark);
  transition: all 0.3s ease;
  padding: 0.5rem;
  border-radius: 12px;
  margin: -0.5rem;
  
  body.dark-mode & {
    color: white;
  }
  
  &:hover {
    background: rgba(0, 0, 0, 0.05);
    transform: translateX(5px);
    
    body.dark-mode & {
      background: rgba(255, 255, 255, 0.1);
    }
  }
`;

const InfoIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: var(--secondary);
  color: var(--dark);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  svg {
    font-size: 1.2rem;
  }
  
  @media (max-width: 480px) {
    width: 42px;
    height: 42px;
  }
`;

const InfoContent = styled.div``;

const InfoLabel = styled.span`
  display: block;
  font-size: 0.8rem;
  color: var(--gray-700);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.2rem;
  font-weight: 600;
  
  body.dark-mode & {
    color: var(--gray-400);
  }
`;

const InfoValue = styled.span`
  font-size: 1rem;
  font-weight: 500;
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(0, 0, 0, 0.1);
  margin: 1.5rem 0;
  
  body.dark-mode & {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const AvailabilityBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(22, 101, 52, 0.15);
  color: #15803d;
  padding: 0.6rem 1rem;
  border-radius: 50px;
  font-size: 0.85rem;
  font-weight: 600;
  
  svg {
    font-size: 1rem;
  }

  body.dark-mode & {
    background: rgba(52, 168, 83, 0.2);
    color: #4ade80;
  }
`;

const ContactFormCard = styled(motion.form)`
  background: white;
  border-radius: 24px;
  padding: 2.5rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  
  body.dark-mode & {
    background: #0e1420;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem;
    border-radius: 16px;
  }
`;

const FormTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--dark);
  margin-bottom: 1.5rem;
  
  body.dark-mode & {
    color: var(--white);
  }
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
  
  &.full-width {
    grid-column: 1 / -1;
  }
`;

const FormLabel = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--dark);
  margin-bottom: 0.5rem;
  
  body.dark-mode & {
    color: var(--gray-300);
  }
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.9rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 1rem;
  font-family: inherit;
  background: #ffffff;
  color: var(--dark);
  transition: all 0.3s ease;
  
  body.dark-mode & {
    background: #101828;
    border-color: rgba(255, 255, 255, 0.12);
    color: var(--white);
  }
  
  &:focus {
    outline: none;
    border-color: var(--secondary);
    background: white;
    box-shadow: 0 0 0 4px rgba(254, 209, 54, 0.1);
    
    body.dark-mode & {
      background: #141c2e;
      box-shadow: 0 0 0 4px rgba(254, 209, 54, 0.15);
    }
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.9rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 1rem;
  font-family: inherit;
  background: #ffffff;
  color: var(--dark);
  transition: all 0.3s ease;
  resize: vertical;
  min-height: 120px;
  
  body.dark-mode & {
    background: #101828;
    border-color: rgba(255, 255, 255, 0.12);
    color: var(--white);
  }
  
  &:focus {
    outline: none;
    border-color: var(--secondary);
    background: white;
    box-shadow: 0 0 0 4px rgba(254, 209, 54, 0.1);
    
    body.dark-mode & {
      background: #141c2e;
      box-shadow: 0 0 0 4px rgba(254, 209, 54, 0.15);
    }
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const SubmitButton = styled(motion.button)`
  width: 100%;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, var(--secondary) 0%, #e6b800 100%);
  color: var(--dark);
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  transition: all 0.3s ease;
  
  svg {
    font-size: 1.1rem;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(254, 209, 54, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const FormMessage = styled(motion.div)`
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 12px;
  text-align: center;
  font-weight: 500;
  
  ${props => props.$success ? `
    background: rgba(52, 168, 83, 0.1);
    color: #22c55e;
    border: 1px solid rgba(52, 168, 83, 0.2);
  ` : `
    background: rgba(220, 53, 69, 0.1);
    color: #ef4444;
    border: 1px solid rgba(220, 53, 69, 0.2);
  `}
`;

const ResponseTime = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
  color: var(--gray-500);
  font-size: 0.85rem;
  
  svg {
    font-size: 1rem;
  }
  
  body.dark-mode & {
    color: var(--gray-400);
  }
`;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  
  const [formStatus, setFormStatus] = useState({
    submitted: false,
    success: false,
    message: ''
  });
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      setFormStatus({
        submitted: true,
        success: false,
        message: 'Veuillez remplir tous les champs obligatoires.'
      });
      return;
    }
    
    const serviceId = 'service_vzakr2l'; // Service EmailJS existant
    const templateId = 'template_5om6n7d'; // Template ID EmailJS
    const userId = 'YmS5IwX0hsLszGDrl'; // Public key EmailJS

    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      phone: formData.phone,
      subject: formData.subject,
      message: formData.message,
    };

    emailjs.send(serviceId, templateId, templateParams, userId)
      .then(() => {
        setFormStatus({
          submitted: true,
          success: true,
          message: 'Message envoyé ! Je vous réponds dans les 24h.'
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      })
      .catch(() => {
        setFormStatus({
          submitted: true,
          success: false,
          message: "Une erreur s'est produite. Veuillez réessayer plus tard."
        });
      });
  };
  
  return (
    <ContactContainer id="contact">
      <SectionStars count={120} />
      <ContactWrapper>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 1, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Discutons de votre <span>projet</span>
          </SectionTitle>
          <SectionSubtitle
            initial={{ opacity: 1, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Une idée ? Un projet ? Contactez-moi pour en discuter.
          </SectionSubtitle>
        </SectionHeader>
        
        <ContactGrid>
          <ContactInfoCard
            initial={{ opacity: 1, x: 0 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <InfoTitle>Informations de contact</InfoTitle>
            <InfoSubtitle>
              N'hésitez pas à me contacter par téléphone, email ou via le formulaire.
            </InfoSubtitle>
            
            <ContactInfoList>
              <ContactInfoItem href="tel:+32471229114">
                <InfoIcon>
                  <FiPhone />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Téléphone</InfoLabel>
                  <InfoValue>+32 471 22 91 14</InfoValue>
                </InfoContent>
              </ContactInfoItem>
              
              <ContactInfoItem href="mailto:mail@gvw-tech.be">
                <InfoIcon>
                  <FiMail />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Email</InfoLabel>
                  <InfoValue>mail@gvw-tech.be</InfoValue>
                </InfoContent>
              </ContactInfoItem>
              
              <ContactInfoItem 
                href="https://maps.google.com/?q=1017+Chaussée+de+Marche+5100+Wierde+Belgique" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <InfoIcon>
                  <FiMapPin />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Adresse</InfoLabel>
                  <InfoValue>Wierde, Belgique</InfoValue>
                </InfoContent>
              </ContactInfoItem>
            </ContactInfoList>
            
            <Divider />
            
            <AvailabilityBadge>
              <FiCheck />
              Disponible pour nouveaux projets
            </AvailabilityBadge>
          </ContactInfoCard>
          
          <ContactFormCard
            onSubmit={handleSubmit}
            initial={{ opacity: 1, x: 0 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <FormTitle>Envoyez-moi un message</FormTitle>
            
            <FormGrid>
              <FormGroup>
                <FormLabel htmlFor="name">Nom *</FormLabel>
                <FormInput 
                  type="text" 
                  id="name" 
                  name="name"
                  placeholder="Votre nom"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="email">Email *</FormLabel>
                <FormInput 
                  type="email" 
                  id="email" 
                  name="email"
                  placeholder="votre@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="phone">Téléphone</FormLabel>
                <FormInput 
                  type="tel" 
                  id="phone" 
                  name="phone"
                  placeholder="+32 ..."
                  value={formData.phone}
                  onChange={handleChange}
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="subject">Sujet</FormLabel>
                <FormInput 
                  type="text" 
                  id="subject" 
                  name="subject"
                  placeholder="Sujet de votre message"
                  value={formData.subject}
                  onChange={handleChange}
                />
              </FormGroup>
              
              <FormGroup className="full-width">
                <FormLabel htmlFor="message">Message *</FormLabel>
                <FormTextarea 
                  id="message" 
                  name="message"
                  placeholder="Décrivez votre projet ou posez votre question..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
            </FormGrid>
            
            <SubmitButton 
              type="submit"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FiSend />
              Envoyer le message
            </SubmitButton>
            
            {formStatus.submitted && (
              <FormMessage 
                $success={formStatus.success}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {formStatus.message}
              </FormMessage>
            )}
            
            <ResponseTime>
              <FiClock />
              Réponse généralement sous 24h
            </ResponseTime>
          </ContactFormCard>
        </ContactGrid>
      </ContactWrapper>
    </ContactContainer>
  );
};

export default ContactSection;
